import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent, trackPrimaryCtaClicked } from '../services/analyticsService';

const MOBILE_QUERY = '(max-width: 767px)';

function getIsMobile() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * Homepage product tour. Click to play with sound (no autoplay), the vertical cut on
 * phones, and a clickable end card that opens sign-up for signed-out visitors.
 * cut: 'en' (English narration, Spanish materials) or 'es' (Spanish narration, English materials).
 */
export default function HeroScreencast({ cut = 'en', showSignup = false, onSignup }) {
  const { t } = useTranslation('pages');
  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => {
      // Never swap the source while the video is playing.
      if (!videoRef.current || videoRef.current.paused) setIsMobile(e.matches);
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const format = isMobile ? '9x16' : '16x9';
  const src = `/video/screencast-${cut}-${format}.mp4`;
  const poster = `/video/screencast-${cut}-${format}.jpg`;
  const analytics = { cut, format, landingPage: typeof window !== 'undefined' ? window.location.pathname : '/' };

  const play = () => {
    const video = videoRef.current;
    if (!video) return;
    if (ended) video.currentTime = 0;
    setStarted(true);
    setEnded(false);
    video.play().catch(() => {});
    trackEvent(ended ? 'screencast_replayed' : 'screencast_played', analytics);
  };

  const handleEnded = () => {
    setEnded(true);
    trackEvent('screencast_completed', analytics);
  };

  const handleSignup = () => {
    trackPrimaryCtaClicked({ source: 'homepage', ctaLocation: 'hero_video_endcard', ...analytics });
    onSignup?.();
  };

  const frameClass = isMobile
    ? 'relative mx-auto aspect-[9/16] h-[min(70dvh,640px)] max-w-full'
    : 'relative mx-auto aspect-video w-full max-w-[1040px]';

  return (
    <div
      className={`${frameClass} overflow-hidden rounded-[28px] bg-[#F9F7F3] shadow-[0_30px_60px_-34px_rgba(28,25,23,0.55)] ring-1 ring-stone-900/5`}
    >
      <video
        key={src}
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        src={src}
        poster={poster}
        preload="none"
        playsInline
        controls={started && !ended}
        onEnded={handleEnded}
        aria-label={t('hero.screencast.ariaLabel', 'Aprendos product tour')}
      />

      {!started && (
        <button
          type="button"
          onClick={play}
          className="group absolute inset-0 flex flex-col items-center justify-center px-6 text-center focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-[#D63AB4]"
          style={{
            containerType: 'size',
            background:
              'radial-gradient(60% 70% at 85% 10%, rgba(214,58,180,0.06), transparent 70%), radial-gradient(55% 60% at 8% 95%, rgba(214,58,180,0.04), transparent 70%), linear-gradient(160deg, #FDFBF7, #F7F2EB)',
          }}
          aria-label={t('hero.screencast.play', 'Watch the 90-second tour')}
        >
          <span
            className="flex items-center gap-[0.6em] font-bold uppercase tracking-[0.18em] text-[#B2288E]"
            style={{ fontFamily: 'var(--font-body)', fontSize: isMobile ? '3.4cqw' : 'max(12px, 1.35cqw)' }}
          >
            <span className="inline-block h-[0.45em] w-[0.45em] rounded-full bg-[#D63AB4]" aria-hidden="true" />
            {t('hero.screencast.eyebrow', 'Product tour')}
          </span>
          <span
            className="mt-[0.35em] block text-stone-900"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              lineHeight: isMobile ? 1.05 : 1,
              letterSpacing: '-0.035em',
              fontSize: isMobile ? '9.6cqw' : '6.8cqw',
            }}
          >
            <span className="block">{t('hero.screencast.titleLine1', 'Discover Aprendos')}</span>
            <span className="block">
              {t('hero.screencast.titlePrefix', 'in')}{' '}
              <em className="italic text-[#D63AB4]">{t('hero.screencast.titleAccent', '90 seconds')}</em>
            </span>
          </span>
          <span
            className="flex items-center justify-center rounded-full bg-[#B2288E] text-white shadow-[0_18px_36px_-14px_rgba(178,40,142,0.8),0_0_0_10px_rgba(214,58,180,0.10)] transition group-hover:scale-105"
            style={{ marginTop: '5.5cqh', width: isMobile ? '10cqh' : '13cqh', height: isMobile ? '10cqh' : '13cqh' }}
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" className="ml-[7%] h-[42%] w-[42%]" fill="currentColor">
              <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11.24-6.86a1 1 0 0 0 0-1.72L9.5 4.28A1 1 0 0 0 8 5.14Z" />
            </svg>
          </span>
          <span
            className="flex max-w-[80%] flex-wrap justify-center gap-y-[0.2em] font-semibold text-stone-600"
            style={{ fontFamily: 'var(--font-body)', marginTop: '5cqh', fontSize: isMobile ? '3.7cqw' : 'max(12px, 1.45cqw)', lineHeight: 1.7 }}
            aria-hidden="true"
          >
            {t('hero.screencast.features', 'Podcast lessons|Exercises|AI tutor|Graded readers|Coach')
              .split('|')
              .map((item, i, list) => (
                <span key={item} className="whitespace-nowrap">
                  {item}
                  {i < list.length - 1 && <span className="mx-[0.55em] font-medium text-stone-300">·</span>}
                </span>
              ))}
          </span>
        </button>
      )}

      {ended && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-stone-950/75 px-6 text-center backdrop-blur-[2px]">
          {showSignup && (
            <>
              <p
                className="text-[clamp(1.6rem,4vw,2.6rem)] leading-tight text-white"
                style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}
              >
                {t('hero.screencast.endTitle', 'Start with one episode today')}
              </p>
              <button
                type="button"
                onClick={handleSignup}
                className="inline-flex min-h-[56px] items-center justify-center whitespace-nowrap rounded-full bg-[#B2288E] px-7 py-4 text-[1rem] sm:px-9 sm:text-[1.08rem] font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#9c2380] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-stone-900"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                {t('hero.screencast.cta', 'Sign up for a free account')}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={play}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 text-[0.98rem] font-semibold text-white/90 underline-offset-4 transition hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" />
            </svg>
            {t('hero.screencast.replay', 'Watch again')}
          </button>
        </div>
      )}
    </div>
  );
}
